import { theme as proTheme } from "@chakra-ui/pro-theme";
import type { StyleFunctionProps, ThemeConfig } from "@chakra-ui/react";
import { extendTheme, withDefaultColorScheme, withDefaultSize } from "@chakra-ui/react";
import { mode, transparentize } from "@chakra-ui/theme-tools";
import { withProse } from "@nikolovlazar/chakra-ui-prose";
import Color from "color";

export const addOpacity = (color: string, opacity: number) =>
  Color(color)
    .fade(1 - opacity)
    .rgb()
    .string();

export const generateAlphaColors = (color: string) => ({
  900: addOpacity(color, 0.92),
  800: addOpacity(color, 0.8),
  700: addOpacity(color, 0.6),
  600: addOpacity(color, 0.48),
  500: addOpacity(color, 0.38),
  400: addOpacity(color, 0.24),
  300: addOpacity(color, 0.16),
  200: addOpacity(color, 0.12),
  100: addOpacity(color, 0.08),
  50: addOpacity(color, 0.04),
});

const config: ThemeConfig = {
  initialColorMode: "dark",
  useSystemColorMode: false,
};

export const theme = extendTheme(
  withProse({
    baseStyle: {
      color: "gray.200",
      fontSize: "sm",
      p: {
        fontSize: "sm",
      },
      a: {
        color: "white",
        textDecorationLine: "underline",
        textDecorationStyle: "dashed",
        textDecorationColor: "whiteAlpha.400",
        textDecorationThickness: "0.1em",
        textUnderlineOffset: "0.1em",
        textUnderlinePosition: "under",
        transition: "text-decoration-color 0.2s",
        _hover: {
          textDecorationLine: "underline",
          textDecorationThickness: "0.1em",
          textUnderlineOffset: "0.1em",
          textUnderlinePosition: "under",
          textDecorationStyle: "dashed",
          textDecorationColor: "brand.400",
        },
      },
      h1: {
        color: "white",
        fontSize: "2xl",
        fontWeight: "semibold",
        borderBottomWidth: 1,
      },
      h2: {
        color: "white",
        fontSize: "xl",
        fontWeight: "semibold",
      },
      h3: {
        color: "white",
        fontSize: "lg",
        fontWeight: "semibold",
      },
      h4: {
        color: "white",
        fontSize: "md",
        fontWeight: "semibold",
      },
    },
  }),
  withDefaultColorScheme({
    colorScheme: "gray",
    components: ["Button"],
  }),
  withDefaultColorScheme({
    colorScheme: "brand",
    components: ["Progress"],
  }),
  withDefaultSize({ size: "sm", components: ["Heading"] }),
  withDefaultSize({ size: "sm", components: ["Button"] }),
  {
    components: {
      Button: {
        baseStyle: {
          fontWeight: "medium",
          letterSpacing: "tight",
          borderRadius: "lg",
          _disabled: {
            opacity: 0.4,
            cursor: "not-allowed",
          },
        },
        variants: {
          // Taken from official Chakra UI theme
          ghost: (props: StyleFunctionProps) => {
            const { colorScheme: c, theme } = props;

            if (c === "gray") {
              return {
                color: mode(`gray.800`, `whiteAlpha.900`)(props),
                _hover: {
                  bg: mode(`gray.100`, `whiteAlpha.200`)(props),
                },
                _active: { bg: mode(`gray.200`, `whiteAlpha.300`)(props) },
              };
            }

            const darkHoverBg = transparentize(`${c}.200`, 0.12)(theme);
            const darkActiveBg = transparentize(`${c}.200`, 0.24)(theme);

            return {
              color: mode(`${c}.600`, `${c}.200`)(props),
              bg: "transparent",
              _hover: {
                bg: mode(`${c}.50`, darkHoverBg)(props),
              },
              _active: {
                bg: mode(`${c}.100`, darkActiveBg)(props),
              },
            };
          },
          solid: {
            borderWidth: 1,
            borderColor: "whiteAlpha.100",
          },
          flat: {
            bgColor: "white",
            color: "gray.800",
            boxShadow: "none",
            fontWeight: "semibold",
            _hover: {
              bgColor: "gray.100",
            },
            _active: {
              bgColor: "gray.200",
            },
          },
          filter: (props: StyleFunctionProps) => ({
            background: "transparent",
            borderColor: `${props.colorScheme}.700`,
            borderStyle: props.colorScheme === "gray" ? "dashed" : "solid",
            color: `${props.colorScheme}.300`,
            borderWidth: 1,
            paddingY: 1.5,

            _hover: {
              background: "gray.700",
            },
            _active: {
              background: "gray.700",
            },
          }),
          primary: {
            bgGradient: "linear(to-b, brand.500, brand.600)",
            color: "white",
            border: "none",
            boxShadow:
              "0 0 0 1px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 2px 1px -1px rgba(255, 255, 255, 0.2)",
            textShadow: "0 1px 1px rgba(0, 0, 0, 0.3)",
            transition: "background 0.4s",

            "& svg": {
              filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.3))",
            },

            _hover: {
              bgGradient: "linear(to-b, brand.400, brand.500)",
              _disabled: {
                bgGradient: "linear(to-b, brand.500, brand.600)",
              },
            },

            _active: {
              bgGradient: "linear(to-b, brand.600, brand.700)",
              opacity: 0.6,

              _disabled: {
                bgGradient: "linear(to-b, brand.500, brand.600)",
                opacity: 0.4,
              },
            },
          },
          secondary: (props: StyleFunctionProps) => ({
            bgColor: `${props.colorScheme}.700`,
            color: "white",
            borderWidth: 1,
            borderColor: "whiteAlpha.300",
            _hover: {
              bgColor: `${props.colorScheme}.500`,
              borderColor: "whiteAlpha.400",
            },
            _active: {
              bgColor: `${props.colorScheme}.500`,
              opacity: 0.6,
            },
          }),
          danger: {
            bgGradient: "linear(to-b, red.500, red.600)",
            color: "white",
            border: "none",
            boxShadow:
              "0 0 0 1px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 2px 1px -1px rgba(255, 255, 255, 0.2)",
            textShadow: "0 1px 1px rgba(0, 0, 0, 0.3)",
            transition: "background 0.4s",

            "& svg": {
              filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.3))",
            },

            _hover: {
              bgGradient: "linear(to-b, red.400, red.500)",
              _disabled: {
                bgGradient: "linear(to-b, red.500, red.600)",
              },
            },

            _active: {
              bgGradient: "linear(to-b, red.600, red.700)",
              opacity: 0.6,

              _disabled: {
                bgGradient: "linear(to-b, red.500, red.600)",
                opacity: 0.4,
              },
            },
          },
          danger2: {
            background: "rgba(237, 74, 74, 0.15)",
            color: "#FF7474",
            border: "1px solid rgba(255, 255, 255, 0.06)",
            transition: "background 0.4s",

            _hover: {
              background: "#ED4A4A",
              color: "white",
              _disabled: {
                background: "#ED4A4A",
              },
            },

            _active: {
              background: "#ED4A4A",
              color: "white",
              opacity: 0.6,

              _disabled: {
                background: "#ED4A4A",
                opacity: 0.4,
              },
            },
          },
        },
      },
      Badge: {
        variants: {
          vibrant: (props: StyleFunctionProps) => ({
            bg: `${props.colorScheme}.300`,
            color: `${props.colorScheme}.900`,
          }),
        },
      },
      Input: {
        baseStyle: {
          field: {
            _placeholder: {
              color: "whiteAlpha.400",
            },
          },
        },
      },
      Select: {
        baseStyle: {
          field: {
            bgColor: "gray.800",
          },
        },
        sizes: {
          sm: {
            field: {
              borderRadius: "md",
            },
          },
        },
        defaultProps: {
          focusBorderColor: "brand.200",
        },
      },
      Modal: {
        defaultProps: {
          isCentered: true,
        },
        baseStyle: {
          dialog: {
            bgColor: "gray.800",
          },
        },
      },
      Table: {
        baseStyle: {
          table: {
            borderRadius: "md",
            overflow: "hidden",
          },
        },
      },
    },
  },
  {
    config,
    styles: {
      global: {
        body: {
          fontSize: "sm",
          lineHeight: "tall",
        },
        "::selection": {
          bg: "brandAlpha.200",
          color: "brand.300",
        },
        a: {
          color: "brand.400",
        },
      },
    },
    layerStyles: {
      card: {
        bg: "gray.800",
        borderWidth: 1,
        borderRadius: "md",
      },
      iconButton: {
        background: "transparent",
        border: "transparent",
        minWidth: "initial",
        width: 9,
        height: 9,
        borderRadius: "full",
      },
    },
  },
  (theme) => ({
    ...theme,

    fonts: {
      ...theme.fonts,
      heading: "var(--font-inter), -apple-system, system-ui, sans-serif",
      body: "var(--font-inter), -apple-system, system-ui, sans-serif",
    },

    colors: {
      ...theme.colors,
      brand: {
        50: "#f5f3ff",
        100: "#ede9fe",
        200: "#ddd6fc",
        300: "#c4b5fa",
        400: "#a78bfa",
        500: "#8b5cf6",
        600: "#7c3aed",
        700: "#6d28d9",
        800: "#5b21b6",
        900: "#4c1d95",
      },
      brandAlpha: generateAlphaColors("#8b5cf6"),
      gray: {
        50: "#f2effc",
        100: "#d5d3e3",
        200: "#b9b5ca",
        300: "#9e98b4",
        400: "#827a9e",
        500: "#686084",
        600: "#514b67",
        700: "#3a364b",
        800: "#23202f",
        850: "#1b1726",
        900: "#0d0a16",
        950: "#06060b",
      },
      redAlpha: generateAlphaColors("#ef4444"),
      greenAlpha: generateAlphaColors("#48BB78"),
      yellowAlpha: generateAlphaColors("#f59e0b"),
      orangeAlpha: generateAlphaColors("#f97316"),
    },

    shadows: {
      ...theme.shadows,
      outline: "0 0 0 3px var(--chakra-colors-brand-200)",
    },

    semanticTokens: {
      ...theme.semanticTokens,
      colors: {
        ...theme.semanticTokens.colors,
        "bg-canvas": "gray.900",
      },
    },
  }),
  proTheme,
);
